/**
 * @fileoverview Custom hook that check if the current job bundle contains all available job upgrades.
 * If yes stores info to the local storage.
 */

import { Product } from '~/jobiqo/types/types';
import { useJobWizardContext } from '~/jobiqo/components/Job/Wizard/context/useJobWizardContext';
import { useLocalStorage } from '~/jobiqo/hooks/useLocalStorage';
import { useQuery } from '@apollo/client';
import GET_JOB_UPGRADES from '~/jobiqo/components/Job/Wizard/Steps/Upgrades/graphql/jobUpgrades.gql';
import { useEffect } from 'react';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

/**
 * The hook check if the current job bundle contains all available job upgrades and stores info to the local storage.
 * @param {Product} currentBundle
 *  The current bundle based on url parameter.
 * @param {string} locale
 *  The locale from app context.
 */
export const useBundleContainsAllUpgrades = (
  currentBundle: Product,
  locale?: string,
): void => {
  const { setShouldShowUpgradesStep } = useJobWizardContext();
  const { JOBS } = useFrontendConfig();
  const [, setShouldSkipUpgradeStep] = useLocalStorage(
    'should-skip-job-upgrade-step',
    null,
  );

  const { data: jobUpgradesData } = useQuery(GET_JOB_UPGRADES, {
    variables: {
      locale: locale,
    },
  });

  useEffect(() => {
    if (
      currentBundle?.relatedJobUpgrades &&
      jobUpgradesData?.jobUpgrades?.length > 0
    ) {
      // Check if bundle contains all available upgrades.
      const allUpgradesIncludedInBundle = jobUpgradesData.jobUpgrades.every(
        (upgrade) =>
          currentBundle?.relatedJobUpgrades?.some(
            (relatedUpgrade) => relatedUpgrade?.id === upgrade.id,
          ),
      );

      if (!JOBS.SHOW_UPGRADES_IN_DESTINATION_STEP) {
        setShouldShowUpgradesStep(!allUpgradesIncludedInBundle);
      }

      // Store value in local storage for use on next steps.
      if (allUpgradesIncludedInBundle === true) {
        setShouldSkipUpgradeStep('skip');
      }
    }
  }, [currentBundle, jobUpgradesData]);
};
